/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import { useEffect, useRef, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { supabase } from '../../shared/supabase/supabaseClient';
import { KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { ticketColors } from '../../shared/theme/ticketColors'
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts';
import { ModalScrollScreen } from '../../shared/layout/ModalScrollScreen';
import { AuthScreenFrame } from '../../shell/brand/AuthScreenFrame';
import { AppLogoMark } from '../../shell/brand/AppLogoMark';
import { showError, showInfo, showSuccess } from '../../shared/ui/appToast';
import { logout } from '../application/authSession';
import { isPasswordRecoveryUrl } from '../application/sso';
import {
  sessionIsPasswordRecovery,
  userFacingPasswordUpdateMessage,
} from '../application/passwordRecoverySession';
import { getPasswordRecoveryFlowUserId } from '../application/passwordRecoveryWebStorage';
import { reportAuthFailure } from '../application/authObservability';
import { AUTH_ACCENT_INK, authFormUi } from './components/authFormUi';
import { AuthWhatsAppHelpLink } from './components/AuthWhatsAppHelpLink';

type Props = {
  onComplete: () => void;
};

type AuthUpdateStub = {
  updateUser: (args: { password: string }) => Promise<{ data: { user: { id: string } | null }; error: Error | null }>;
};

function resolveAuthApi(): {
  updateUser: (args: { password: string }) => Promise<{ data: { user: { id: string } | null }; error: { message?: string } | null }>;
  getSession: () => Promise<{ data: { session: import('@supabase/supabase-js').Session | null } }>;
} {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const stub = (window as unknown as { __E2E_AUTH_STUB__?: AuthUpdateStub }).__E2E_AUTH_STUB__;
    if (stub?.updateUser) {
      return {
        updateUser: stub.updateUser,
        getSession: async () => ({ data: { session: { user: { id: 'e2e-stub' } } as never } }),
      };
    }
  }
  return supabase.auth;
}

/**
 * Usada solo en **Expo web** (PWA, p. ej. app.vuelto.ar): el mail aterriza con sesión
 * de recovery; en iOS/Android el flujo se abre en el navegador, no en esta pantalla.
 */
export function PasswordRecoveryScreen({ onComplete }: Props) {
  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const scrollContentRef = useRef<View>(null);
  const passwordInputRef = useRef<TextInput>(null);
  const confirmInputRef = useRef<TextInput>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    ensureTicketFonts();
  }, []);

  const submit = async () => {
    setErr(null);
    if (password.length < 8) {
      const m = 'La contraseña debe tener al menos 8 caracteres.';
      setErr(m);
      showInfo('Contraseña', m);
      return;
    }
    if (password !== confirm) {
      const m = 'Las contraseñas no coinciden.';
      setErr(m);
      showInfo('Revisá', m);
      return;
    }
    const { isImpersonating } = await import('../../shell/support/useImpersonationStore');
    if (isImpersonating()) {
      const m = 'No disponible en modo soporte (impersonación).';
      setErr(m);
      showInfo('Modo soporte', m);
      return;
    }

    const authApi = resolveAuthApi();
    const {
      data: { session: before },
    } = await authApi.getSession();
    const e2eStub = Platform.OS === 'web' && typeof window !== 'undefined'
      && Boolean((window as unknown as { __E2E_AUTH_STUB__?: unknown }).__E2E_AUTH_STUB__)
      && !(window as unknown as { __E2E_STRICT_RECOVERY__?: boolean }).__E2E_STRICT_RECOVERY__;
    const recoveryMarkerMatchesUser = Boolean(
      before?.user?.id && getPasswordRecoveryFlowUserId() === before.user.id,
    );
    if (!e2eStub && !sessionIsPasswordRecovery(before) && !recoveryMarkerMatchesUser) {
      const m = 'El enlace ya no vale. Pedí uno nuevo desde el login.';
      setErr(m);
      showError('Enlace inválido', m);
      reportAuthFailure('update_password', new Error(m), { expected: true });
      return;
    }

    setSaving(true);
    const { data, error } = await authApi.updateUser({ password });
    if (error) {
      setSaving(false);
      const m = userFacingPasswordUpdateMessage(error.message ?? '');
      setErr(m);
      showError('No se pudo guardar', m);
      reportAuthFailure('update_password', error, { title: 'No se pudo guardar' });
      return;
    }
    if (!data?.user?.id) {
      setSaving(false);
      const m = 'No pudimos confirmar el cambio. Pedí un enlace nuevo e intentá de nuevo.';
      setErr(m);
      showError('No se pudo guardar', m);
      reportAuthFailure('update_password', new Error(m), { title: 'No se pudo guardar' });
      return;
    }

    showSuccess('Contraseña actualizada', 'Ya podés iniciar sesión con la nueva clave.');
    await logout('password_recovered');
    if (Platform.OS === 'web' && typeof window !== 'undefined' && isPasswordRecoveryUrl(window.location.href)) {
      const { origin } = window.location;
      window.history.replaceState(null, '', `${origin}/`);
    }
    onComplete();
    setSaving(false);
  };

  return (
    <AuthScreenFrame>
      <ModalScrollScreen
        ref={scrollRef}
        contentRef={scrollContentRef}
        variant="auth"
        contentContainerStyle={authFormUi.scrollCenter}
        keyboardShouldPersistTaps="handled"
      >
        <View style={authFormUi.card}>
          <View style={authFormUi.brandBlock}>
            <AppLogoMark size={56} showWordmark />
          </View>

          <View style={styles.head}>
            <Text style={authFormUi.title}>Nueva contraseña</Text>
            <Text style={authFormUi.subtitle}>
              Mínimo 8 caracteres. Después vas a poder ingresar con la clave nueva.
            </Text>
          </View>

          <View style={authFormUi.fieldGroup}>
            <Text style={authFormUi.label}>Nueva contraseña</Text>
            <View style={authFormUi.passwordRow}>
              <TextInput
                ref={passwordInputRef}
                style={[authFormUi.input, authFormUi.passwordInput, err ? authFormUi.inputError : null]}
                value={password}
                onChangeText={setPassword}
                placeholder="Ingresá tu nueva clave"
                placeholderTextColor={ticketColors.ink3}
                secureTextEntry={!showPassword}
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="new-password"
                textContentType="newPassword"
                returnKeyType="next"
                blurOnSubmit={false}
                onSubmitEditing={() => confirmInputRef.current?.focus()}
                accessibilityLabel="Nueva contraseña"
              />
              <Pressable
                onPress={() => setShowPassword((s) => !s)}
                style={({ pressed }) => [authFormUi.eyeButton, pressed && styles.eyePressed]}
                accessibilityRole="button"
                accessibilityLabel={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                hitSlop={8}
              >
                <Ionicons
                  name={showPassword ? 'eye-off-outline' : 'eye-outline'}
                  size={20}
                  color={ticketColors.ink3}
                />
              </Pressable>
            </View>
          </View>

          <View style={authFormUi.fieldGroup}>
            <Text style={authFormUi.label}>Repetí la contraseña</Text>
            <View style={authFormUi.passwordRow}>
              <TextInput
                ref={confirmInputRef}
                style={[authFormUi.input, authFormUi.passwordInput]}
                value={confirm}
                onChangeText={setConfirm}
                placeholder="Volvé a ingresarla"
                placeholderTextColor={ticketColors.ink3}
                secureTextEntry={!showConfirm}
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="new-password"
                textContentType="newPassword"
                returnKeyType="done"
                onSubmitEditing={() => {
                  if (!saving) void submit();
                }}
                accessibilityLabel="Repetí la contraseña"
              />
              <Pressable
                onPress={() => setShowConfirm((s) => !s)}
                style={({ pressed }) => [authFormUi.eyeButton, pressed && styles.eyePressed]}
                accessibilityRole="button"
                accessibilityLabel={showConfirm ? 'Ocultar repetición de contraseña' : 'Mostrar repetición de contraseña'}
                hitSlop={8}
              >
                <Ionicons
                  name={showConfirm ? 'eye-off-outline' : 'eye-outline'}
                  size={20}
                  color={ticketColors.ink3}
                />
              </Pressable>
            </View>
          </View>

          {err ? (
            <View style={authFormUi.errorBanner} accessibilityRole="alert">
              <Text style={authFormUi.errorBannerText}>{err}</Text>
            </View>
          ) : null}

          <TouchableOpacity
            style={[authFormUi.primaryBtn, saving && authFormUi.primaryBtnDisabled]}
            onPress={() => void submit()}
            disabled={saving}
            accessibilityRole="button"
            accessibilityLabel="Guardar contraseña"
          >
            {saving ? (
              <ActivityIndicator color={AUTH_ACCENT_INK} />
            ) : (
              <Text style={authFormUi.primaryLabel}>Guardar contraseña</Text>
            )}
          </TouchableOpacity>

          <View style={authFormUi.links}>
            <AuthWhatsAppHelpLink testID="auth.recovery-whatsapp" />
          </View>
        </View>
      </ModalScrollScreen>
    </AuthScreenFrame>
  );
}

const styles = StyleSheet.create({
  head: {
    gap: 8,
  },
  eyePressed: {
    opacity: 0.7,
  },
});
